import React, { useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Mention from '@tiptap/extension-mention';
import EditorToolbar from './EditorToolbar';
import { mentionSuggestion } from './mentionSuggestion';
import type { MentionUser } from './mentionSuggestion';
import { richTextToHtml } from './richText';
import './editor.css';

export type { MentionUser };

export type RichTextEditorProps = {
  /** HTML, or legacy Editor.js JSON — both are understood. */
  value: string;
  onChange: (html: string) => void;
  /** Ctrl/Cmd+Enter. */
  onSave?: (html: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  autoFocus?: boolean;
  minHeight?: number;
  /** Returns the URL to insert. Without it images are refused instead of being inlined as base64. */
  uploadImage?: (file: File) => Promise<string>;
  mentionUsers?: MentionUser[];
  className?: string;
};

/** Mirrors `SkTech.Server/Common/ImageUploadRules`. */
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

const DEFAULT_MIN_HEIGHT = 160;

/** The only schemes a stored link may carry — everything else (`javascript:`, `data:`) is dropped. */
const SAFE_LINK_SCHEME = /^(https?:|mailto:)/i;

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onSave,
  placeholder,
  readOnly = false,
  autoFocus = false,
  minHeight = DEFAULT_MIN_HEIGHT,
  uploadImage,
  mentionUsers,
  className,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The editor instance is created once, so everything mutable is read through a ref.
  const editorRef = useRef<Editor | null>(null);
  const onChangeRef = useRef(onChange);
  const onSaveRef = useRef(onSave);
  const uploadImageRef = useRef(uploadImage);
  const mentionUsersRef = useRef<MentionUser[]>(mentionUsers ?? []);
  const tRef = useRef(t);

  useEffect(() => {
    onChangeRef.current = onChange;
    onSaveRef.current = onSave;
    uploadImageRef.current = uploadImage;
    mentionUsersRef.current = mentionUsers ?? [];
    tRef.current = t;
  });

  const uploadOne = useCallback(async (file: File) => {
    const translate = tRef.current;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type) || file.size <= 0 || file.size > MAX_IMAGE_BYTES) {
      message.warning(translate('editorImageRejected'));
      return;
    }
    const upload = uploadImageRef.current;
    if (!upload) {
      message.warning(translate('editorImageRejected'));
      return;
    }
    const key = `editor-image-${Date.now()}-${Math.random()}`;
    message.loading({ content: translate('editorImageUploading'), key, duration: 0 });
    try {
      const url = await upload(file);
      message.destroy(key);
      editorRef.current?.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch {
      message.destroy(key);
      message.error(translate('editorImageUploadFailed'));
    }
  }, []);

  /** Returns true once it takes ownership of the event, false to let ProseMirror handle it. */
  const handleFiles = useCallback(
    (files?: FileList | null): boolean => {
      const images = Array.from(files ?? []).filter((file) => file.type.startsWith('image/'));
      if (!images.length) {
        return false;
      }
      images.forEach((file) => {
        void uploadOne(file);
      });
      return true;
    },
    [uploadOne],
  );

  const handleFilesRef = useRef(handleFiles);
  useEffect(() => {
    handleFilesRef.current = handleFiles;
  }, [handleFiles]);

  const editor = useEditor({
    editable: !readOnly,
    autofocus: autoFocus ? 'end' : false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        protocols: ['http', 'https', 'mailto'],
        // `protocols` only widens the default set — it does not narrow it, so `javascript:` and
        // friends still have to be refused here.
        isAllowedUri: (url, ctx) => ctx.defaultValidate(url) && SAFE_LINK_SCHEME.test(url),
      }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
        suggestion: mentionSuggestion(() => mentionUsersRef.current),
      }),
    ],
    content: richTextToHtml(value),
    editorProps: {
      attributes: {
        class: 'rich-text rich-text-editor',
        style: `min-height:${minHeight}px`,
      },
      handlePaste: (_view, event) => handleFilesRef.current(event.clipboardData?.files),
      handleDrop: (_view, event) => handleFilesRef.current((event as DragEvent).dataTransfer?.files),
      transformPastedHTML: (html) => html.replace(/<img[^>]+src=["']data:[^"']*["'][^>]*>/gi, ''),
      handleKeyDown: (_view, event) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && onSaveRef.current && editorRef.current) {
          onSaveRef.current(editorRef.current.getHTML());
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: instance }) => onChangeRef.current(instance.getHTML()),
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  // Content replaced from the outside (switching task, server refresh) — never while typing.
  useEffect(() => {
    if (!editor) {
      return;
    }
    const next = richTextToHtml(value);
    if (next !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(next, false);
    }
  }, [editor, value]);

  return (
    <div className={className ? `editor-frame ${className}` : 'editor-frame'}>
      {!readOnly && editor ? (
        <EditorToolbar editor={editor} onPickImage={() => fileInputRef.current?.click()} />
      ) : null}
      <EditorContent editor={editor} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </div>
  );
};

export default RichTextEditor;
